import React from 'react';
import { Link } from 'react-router-dom';
import { useAdsActivityStats } from '../../../shared/hooks/UseAdsActivities';

const AdsActivityStats = () => {
  const [{ stats, recentActivities, loading }] = useAdsActivityStats();

  if (loading) {
    return (
      <div className='row'>
        {[...Array(5)].map((_, i) => (
          <div key={i} className='col-md-6 col-xl-2 mb-3'>
            <div className='card'>
              <div className='card-body text-center'>
                <div className='placeholder-glow'>
                  <span className='placeholder col-8'></span>
                  <br />
                  <span className='placeholder col-4'></span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Activities',
      value: stats.total || 0,
      icon: 'fas fa-clipboard-list',
      color: 'primary',
      link: '/ads-activities',
    },
    {
      title: 'Pending',
      value: stats.pending || 0,
      icon: 'fas fa-clock',
      color: 'warning',
      link: '/ads-activities?status=PENDING',
    },
    {
      title: 'In Progress',
      value: stats.inProgress || 0,
      icon: 'fas fa-spinner',
      color: 'info',
      link: '/ads-activities?status=IN_PROGRESS',
    },
    {
      title: 'Completed',
      value: stats.completed || 0,
      icon: 'fas fa-check-circle',
      color: 'success',
      link: '/ads-activities?status=COMPLETED',
    },
    {
      title: 'Facebook Ads',
      value: stats.facebook || 0,
      icon: 'fab fa-facebook',
      color: 'primary',
      link: '/ads-activities?platform=FACEBOOK',
    },
    {
      title: 'Google Ads',
      value: stats.google || 0,
      icon: 'fab fa-google',
      color: 'danger',
      link: '/ads-activities?platform=GOOGLE',
    },
  ];

  return (
    <div className='sidebar-stats'>
      {/* Stats Cards */}
      <div className='stats-grid'>
        {statCards.map((card, index) => (
          <div key={index} className='stats-card-wrapper'>
            <Link to={card.link} className='text-decoration-none'>
              <div className={`stats-card stats-card-${card.color}`}>
                <div className='stats-card-content'>
                  <div className='stats-icon'>
                    <i className={card.icon}></i>
                  </div>
                  <div className='stats-info'>
                    <div className='stats-value'>{card.value}</div>
                    <div className='stats-title'>{card.title}</div>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        ))}
      </div>

      {/* Recent Activities */}
      {recentActivities && recentActivities.length > 0 && (
        <div className='recent-activities mt-3'>
          <div className='recent-header mb-2'>
            <h6 className='mb-0 fw-semibold'>
              <i className='fas fa-history me-2 text-primary'></i>
              Recent Activities
            </h6>
          </div>
          <div className='recent-list'>
            {recentActivities.slice(0, 3).map((activity) => (
              <Link
                key={activity._id}
                to={`/ads-activities/${activity._id}`}
                className='recent-item'
              >
                <div className='recent-item-content'>
                  <div className='recent-item-header'>
                    <i
                      className={`${
                        activity.platform === 'FACEBOOK'
                          ? 'fab fa-facebook'
                          : 'fab fa-google'
                      } platform-icon`}
                      style={{
                        color:
                          activity.platform === 'FACEBOOK'
                            ? '#1877f2'
                            : '#4285f4',
                      }}
                    ></i>
                    <span className='recent-title'>{activity.title}</span>
                  </div>
                  <div className='recent-meta'>
                    <small className='text-muted'>
                      {activity.client?.name}
                    </small>
                    <span
                      className={`badge badge-sm bg-${
                        activity.status === 'PENDING'
                          ? 'warning'
                          : activity.status === 'IN_PROGRESS'
                          ? 'info'
                          : activity.status === 'COMPLETED'
                          ? 'success'
                          : 'secondary'
                      }`}
                    >
                      {activity.status}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <style jsx>{`
        .sidebar-stats {
          width: 100%;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 10px;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;

          flex: 2;
        }

        .stats-card-wrapper {
          width: 100%;
        }

        .stats-card {
          background: white;
          border: 1px solid #e9ecef;
          border-radius: 6px;
          padding: 12px;
          transition: all 0.2s ease;
          height: 100%;
          min-height: 70px;
          display: flex;
          align-items: center;
        }

        .stats-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          border-color: #dee2e6;
        }

        .stats-card-content {
          display: flex;
          align-items: center;
          width: 100%;
          gap: 10px;
        }

        .stats-icon {
          flex-shrink: 0;
          width: 35px;
          height: 35px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
        }

        .stats-card-primary .stats-icon {
          background: rgba(13, 110, 253, 0.1);
          color: #0d6efd;
        }

        .stats-card-warning .stats-icon {
          background: rgba(255, 193, 7, 0.1);
          color: #ffc107;
        }

        .stats-card-info .stats-icon {
          background: rgba(13, 202, 240, 0.1);
          color: #0dcaf0;
        }

        .stats-card-success .stats-icon {
          background: rgba(25, 135, 84, 0.1);
          color: #198754;
        }

        .stats-card-danger .stats-icon {
          background: rgba(220, 53, 69, 0.1);
          color: #dc3545;
        }

        .stats-info {
          flex: 1;
          min-width: 0;
        }

        .stats-value {
          font-size: 1.25rem;
          font-weight: 600;
          line-height: 1;
          color: #2d3748;
        }

        .stats-title {
          font-size: 0.75rem;
          color: #6c757d;
          font-weight: 500;
          line-height: 1.2;
          margin-top: 2px;
        }

        .recent-activities {
          background: white;
          border: 1px solid #e9ecef;
          border-radius: 6px;
          padding: 12px;
          flex: 1;

          border: 1px solid #e9ecef;
          border-radius: 6px;
          background-color: #f8f9fa;
        }

        .recent-header {
          border-bottom: 1px solid #f1f3f4;
          padding-bottom: 8px;
        }

        .recent-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-top: 8px;
        }

        .recent-item {
          display: block;
          padding: 8px;
          border-radius: 4px;
          text-decoration: none;
          color: inherit;
          transition: background-color 0.15s ease;
        }

        .recent-item:hover {
          background-color: #f8f9fa;
          color: inherit;
        }

        .recent-item-content {
          width: 100%;
        }

        .recent-item-header {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 4px;
        }

        .platform-icon {
          font-size: 12px;
          flex-shrink: 0;
        }

        .recent-title {
          font-size: 0.875rem;
          font-weight: 500;
          color: #2d3748;
          line-height: 1.2;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          flex: 1;
        }

        .recent-meta {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 8px;
        }

        .badge-sm {
          font-size: 0.65rem;
          padding: 0.2em 0.4em;
        }

        @media (max-width: 575.98px) {
          .stats-grid {
            grid-template-columns: 1fr;
          }

          .stats-card {
            min-height: 60px;
            padding: 10px;
          }

          .recent-activities {
            padding: 10px;
          }
        }
      `}</style>
    </div>
  );
};

export default AdsActivityStats;
